import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode, type TouchEvent as RTouchEvent } from 'react'
import { createPortal } from 'react-dom'
import { animate, motion, useMotionValue, useReducedMotion, useTransform, type AnimationPlaybackControls } from 'motion/react'
import { t } from '../copy'
import { IconButton } from './Button'
import { Close } from './icons'
import { INTERACTIVE, project, rubberband, VelocityTracker } from './physics'
import { useVisibleBottom } from './useVisibleBottom'

type Props = {
  open: boolean; onClose: () => void; label: string; children: ReactNode
  /**
   * A footer pinned under a scrolling body (long sheets: the draft, the calendar review). The body scrolls on its
   * own, never selects text on a drag, and the sheet is dragged by its handle.
   */
  footer?: ReactNode
  /** While true the sheet can't be dismissed (no scrim tap, Escape, close button or drag): a save is landing. */
  locked?: boolean
  /** The review layer's view name (R-70): pins posted inside this sheet show only while it is open. */
  reviewState?: string
}

/**
 * Bottom sheet: slides up over a dimmed page, follows the finger 1:1 when dragged, rubber-bands upward,
 * and dismisses when the projected release position passes 35% of its height. Every animation starts
 * from the live value, so it can be grabbed mid-flight. Reduced motion: cross-fade, no drag.
 */
export function Sheet({ open, onClose, label, children, footer, locked = false, reviewState }: Props) {
  const reduce = useReducedMotion() ?? false
  const [mounted, setMounted] = useState(open)
  if (open && !mounted) setMounted(true)

  const covered = useVisibleBottom(open) // B-147: stand above a browser toolbar or the keyboard
  const y = useMotionValue(0)
  const fade = useMotionValue(0)
  const height = useRef(480)
  const panel = useRef<HTMLDivElement>(null)
  const anims = useRef<AnimationPlaybackControls[]>([])
  const closeVelocity = useRef(0)
  const shown = useRef(false)
  const drag = useRef<{ id: number; startY: number; startOffset: number } | null>(null)
  const tracker = useRef(new VelocityTracker())
  const closeRef = useRef(onClose)
  const lockedRef = useRef(locked)
  useLayoutEffect(() => {
    lockedRef.current = locked
    closeRef.current = () => {
      if (!lockedRef.current) onClose()
    }
  })

  const scrim = useTransform(() => {
    const f = fade.get()
    const offset = y.get()
    return f * (1 - Math.min(1, Math.max(0, offset) / height.current))
  })

  const stop = () => {
    anims.current.forEach((a) => a.stop())
    anims.current = []
  }

  useLayoutEffect(() => {
    if (!mounted || !panel.current) return
    height.current = panel.current.offsetHeight || height.current
    stop()
    if (open) {
      if (!shown.current) {
        shown.current = true
        fade.set(reduce ? 0 : 0.001)
        y.set(reduce ? 0 : height.current)
      }
      anims.current = reduce
        ? [animate(fade, 1, { duration: 0.2 })]
        : [animate(fade, 1, { duration: 0.2 }), animate(y, 0, { type: 'spring', bounce: 0.15, duration: 0.4 })]
      return
    }
    if (!shown.current) return
    const v = closeVelocity.current
    closeVelocity.current = 0
    const a = reduce
      ? animate(fade, 0, { duration: 0.18 })
      : animate(y, height.current, { type: 'spring', bounce: 0, duration: 0.35, velocity: v })
    anims.current = [a]
    let cancelled = false
    a.then(() => {
      if (cancelled) return
      shown.current = false
      setMounted(false)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mounted, reduce])

  // Focus, Escape and scroll lock while open.
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panel.current?.focus({ preventScroll: true })
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      previous?.focus?.({ preventScroll: true })
    }
  }, [open])

  const onPointerDown = (e: RPointerEvent<HTMLDivElement>) => {
    if (reduce || !open || lockedRef.current) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if ((e.target as Element).closest(INTERACTIVE)) return
    stop()
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { id: e.pointerId, startY: e.clientY, startOffset: y.get() }
    tracker.current.reset(e.timeStamp, 0, e.clientY)
  }

  const onPointerMove = (e: RPointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    const raw = d.startOffset + (e.clientY - d.startY)
    y.set(raw < 0 ? -rubberband(-raw, height.current) : raw)
    tracker.current.add(e.timeStamp, 0, e.clientY)
  }

  const onPointerUp = (e: RPointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    drag.current = null
    const { vy } = e.type === 'pointercancel' ? { vy: 0 } : tracker.current.velocity(e.timeStamp)
    if (y.get() + project(vy) > height.current * 0.35) {
      closeVelocity.current = vy
      closeRef.current()
    } else {
      anims.current = [animate(y, 0, { type: 'spring', bounce: 0, duration: 0.4, velocity: vy })]
    }
  }

  // QA prod r1 (B-068): a long sheet also closes when its scrolled-to-top body is pulled down (the body pans natively, so
  // this follows touch events, not pointer capture); the handle row and the close button stay above the body.
  const pull = useRef<{ y0: number; on: boolean } | null>(null)
  const onBodyTouchStart = (e: RTouchEvent<HTMLDivElement>) => {
    if (reduce || lockedRef.current || e.touches.length !== 1 || e.currentTarget.scrollTop > 0) { pull.current = null; return }
    pull.current = { y0: e.touches[0].clientY, on: false }
    tracker.current.reset(e.timeStamp, 0, e.touches[0].clientY)
  }
  const onBodyTouchMove = (e: RTouchEvent<HTMLDivElement>) => {
    const p = pull.current
    if (!p) return
    const dy = e.touches[0].clientY - p.y0
    if (!p.on && (dy < 0 || e.currentTarget.scrollTop > 0)) { pull.current = null; return }
    if (!p.on && dy > 10) { p.on = true; stop() }
    if (p.on) { y.set(Math.max(0, dy - 10)); tracker.current.add(e.timeStamp, 0, e.touches[0].clientY) }
  }
  const onBodyTouchEnd = (e: RTouchEvent<HTMLDivElement>) => {
    const p = pull.current
    pull.current = null
    if (!p?.on) return
    const { vy } = tracker.current.velocity(e.timeStamp)
    if (y.get() + project(vy) > height.current * 0.25) { closeVelocity.current = vy; closeRef.current() }
    else anims.current = [animate(y, 0, { type: 'spring', bounce: 0, duration: 0.4, velocity: vy })]
  }

  if (!mounted) return null

  return createPortal(
    <div className="fixed inset-0 z-50">
      <motion.div aria-hidden="true" className="absolute inset-0 touch-none bg-scrim" style={{ opacity: scrim }} onClick={() => closeRef.current()} />
      <motion.div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-review-state={reviewState}
        tabIndex={-1}
        className={`absolute inset-x-0 bottom-0 mx-auto max-w-md touch-none rounded-t-[28px] bg-surface shadow-card outline-none after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-[50vh] after:bg-surface ${footer ? 'flex max-h-[calc(100svh-env(safe-area-inset-top)-24px)] flex-col' : ''}`}
        style={{ y, opacity: reduce ? fade : 1, bottom: covered || undefined }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className={`flex shrink-0 justify-center pt-2 ${footer ? 'h-12' : ''}`} aria-hidden="true">
          <span className="h-1.5 w-10 rounded-full bg-line" />
        </div>
        {!locked && (
          <div className="absolute right-2 top-1 z-10">
            <IconButton label={t('nav.close')} onClick={() => closeRef.current()}>
              <Close size={20} />
            </IconButton>
          </div>
        )}
        {footer ? (
          <>
            <div data-no-drag onTouchStart={onBodyTouchStart} onTouchMove={onBodyTouchMove} onTouchEnd={onBodyTouchEnd} onTouchCancel={onBodyTouchEnd}
              className="min-h-0 flex-1 touch-pan-y select-none overflow-y-auto overscroll-contain px-5 pb-3 pt-1 [-webkit-touch-callout:none]">{children}</div>
            <div data-sheet-footer className="shrink-0 border-t border-line px-5 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-3">{footer}</div>
          </>
        ) : (
          <div className="px-5 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-4">{children}</div>
        )}
      </motion.div>
    </div>,
    document.body,
  )
}
